<!--
  CloseRunSummary.vue
  Callbot monthly settlement - the compact "auto close" card at the top of the monthly list (screen spec 3 first
  item). It lived on the default-rate page since revision 5; on 2026-10-27 the user moved it back to the top of the
  list, where it follows the list's month (no month picker of its own).

  Request: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 3", "요청 계약 4-1", trade-off "목록에서 돈과 마감을 뺐다"
  - One line: "자동 마감 · {M}월", then the status counts (확정 n · 검토 필요 n · 실패 n, plus 마감 중 / 추정 중 when > 0)
    once a run exists, or the scheduled / waiting / not-eligible / disabled sentence (settlementRules.autoCloseSummaryText,
    tone from autoCloseSummaryTone) before any run. Small flags mark a manual run (last_run.trigger 'manual', or a run
    started from this card), an interrupted run and auto close switched off (lastRunKind).
    Months before the earliest default-rate version (firstPlanMonth) and months whose scheduled_at is null say they are
    not auto-close targets.
  - Settlement start: '정산 대상 아님 n곳' (counts.before_start, only when > 0) - those companies are skipped by the
    close and are not in the status counts.
  - Tooltip (info icon): counts with the auto / human split and the total, then the last run record
    (auto/manual, one company, catch-up, targets, skipped, interruption reason - lastRunTooltipLines).
  - Hospitals that need a person: attention (close failed, close interrupted, review, reopened) as links to that
    hospital's month (no tab = settlement tab), grouped by status; the tooltip holds the remaining blocking warnings or
    the failure / interruption reason (`error_message`). Interrupted = a `closing` whose lease expired (reply table 8 -
    the detail only shows it as "마감 중"). Deleted companies (`company_deleted`) get a "삭제된 회사" suffix (reply table 6);
    their detail opens like any other (reply "2026-10-19").
  - [지금 마감 실행]: ended months only (otherwise disabled with the reason as tooltip - closeRunDisabledReason).
    Confirm dialog -> POST close-runs (202 run_id). The run is async, so results come with a refresh (no polling);
    right after starting it reloads once to show the "마감 중" count. 503 BILLING_INDEXES_NOT_READY shows a fixed
    operator text (reply table 11). The run notice alert (with its own refresh) stays until the run is seen finished.

  Data: GET /api/v1/callbot/billing/close-runs?month= (DB only), POST /api/v1/callbot/billing/close-runs
  props: api (getSettlementApi()) · month ('YYYY-MM') · firstPlanMonth (earliest default-rate version month, '' if unknown)
  emits: loaded(closeRuns) - expose: reload()
-->
<template>
  <section class="close-card" aria-labelledby="close-card-title" :aria-busy="loading ? 'true' : 'false'">
    <div class="close-card__main">
      <h2 id="close-card-title" class="close-card__title" :title="monthLabel(month)">자동 마감 · {{ shortMonth }}</h2>

      <div class="close-card__status">
        <span v-if="unavailable" class="close-card__state" role="status">
          {{ UNAVAILABLE_TEXT }}
          <span v-if="SETTLEMENT_MOCK_HINT" class="close-card__state-hint">{{ SETTLEMENT_MOCK_HINT }}</span>
        </span>
        <span v-else-if="loadError" class="close-card__state is-error" role="alert">
          {{ loadError }}
          <a-button size="small" :loading="loading" @click="reload">다시 시도</a-button>
        </span>
        <a-skeleton-input v-else-if="!closeRuns" active size="small" class="close-card__skeleton" />

        <template v-else>
          <ul v-if="countItems.length" class="close-card__counts" aria-label="마감 상태별 병원 수">
            <li
              v-for="item in countItems"
              :key="item.key"
              class="close-card__count"
              :class="[`is-${item.tone}`, { 'is-zero': item.value === 0 }]"
            >
              <span class="close-card__dot" aria-hidden="true"></span>
              {{ item.label }} <strong>{{ formatNumber(item.value) }}</strong>
            </li>
          </ul>
          <span v-if="!hasRun" class="close-card__text" :class="`is-${lineTone}`">{{ summaryText }}</span>
          <span v-for="flag in flags" :key="flag.key" class="close-card__flag" :class="`is-${flag.tone}`">
            {{ flag.label }}
          </span>
          <!-- Before the settlement start date - not closed at all, so outside the counts above -->
          <a-tooltip v-if="beforeStartCount" :title="BEFORE_START_TOOLTIP">
            <span class="close-card__flag" tabindex="0">정산 대상 아님 {{ formatNumber(beforeStartCount) }}곳</span>
          </a-tooltip>
          <a-tooltip v-if="detailLines.length" placement="bottomLeft">
            <template #title>
              <div v-for="(line, i) in detailLines" :key="i">{{ line }}</div>
            </template>
            <InfoCircleOutlined class="close-card__help" tabindex="0" role="img" :aria-label="detailLines.join(' ')" />
          </a-tooltip>
          <a-spin v-if="loading" size="small" class="close-card__spin" />
        </template>
      </div>

      <a-tooltip :title="runTooltip">
        <a-button size="small" class="close-card__run" :disabled="!canRun" :loading="starting" @click="confirmRun">
          지금 마감 실행
        </a-button>
      </a-tooltip>
    </div>

    <div v-if="closeRuns && attentionGroups.length" class="close-card__attention">
      <span class="close-card__label">
        사람이 볼 병원
        <a-tooltip :title="ATTENTION_HELP">
          <InfoCircleOutlined class="close-card__help" tabindex="0" role="img" aria-label="사람이 볼 병원 설명" />
        </a-tooltip>
      </span>
      <div v-for="group in attentionGroups" :key="group.key" class="close-card__group">
        <span class="close-card__group-label" :class="`is-${group.key}`">{{ group.label }}</span>
        <ul class="close-card__links" :aria-label="`${group.label} 병원`">
          <li v-for="chip in group.items" :key="chip.key">
            <a-tooltip :title="chip.tooltip">
              <router-link v-if="chip.to" :to="chip.to" class="attention-link" :class="`is-${chip.status}`">
                {{ chip.text }}
              </router-link>
              <span v-else class="attention-link is-plain" :class="`is-${chip.status}`">{{ chip.text }}</span>
            </a-tooltip>
            <span v-if="chip.deleted" class="attention-link__deleted">{{ DELETED_COMPANY_LABEL }}</span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Notice after a manual run started or finished - only while that month is shown -->
    <a-alert
      v-if="runNotice && runNotice.month === month"
      class="close-card__notice"
      :type="runNotice.state === 'done' ? 'success' : 'info'"
      show-icon
      :closable="runNotice.state === 'done'"
      @close="runNotice = null"
    >
      <template #message>{{ runNoticeText }}</template>
      <template v-if="runNotice.state === 'running'" #action>
        <a-button size="small" :loading="loading" @click="reload">새로고침</a-button>
      </template>
    </a-alert>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { Modal, message } from 'ant-design-vue';
import { InfoCircleOutlined } from '@ant-design/icons-vue';

import { formatNumber, isNumber, isValidMonth, monthLabel } from '../service/settlementFormat';
import {
  BILLING_INDEXES_NOT_READY_TEXT,
  CLOSE_INTERRUPTED_LABEL,
  DELETED_COMPANY_LABEL,
  attentionChip,
  autoCloseSummaryText,
  autoCloseSummaryTone,
  closeCountsText,
  closeRunConfirmText,
  closeRunDisabledReason,
  lastRunKind,
  lastRunTooltipLines,
  settlementDetailLocation,
} from '../service/settlementRules';
import { SETTLEMENT_MOCK_HINT } from '../service/settlementMock';
import {
  getCallApiErrorCode,
  isCallApiUnimplemented,
  parseCallApiError,
} from '@/views/Callbot/service/callApiError';

const props = defineProps({
  /** getSettlementApi() - the same-shaped mock under ?settlementMock=1 */
  api: { type: Object, required: true },
  /** Target month 'YYYY-MM' (KST) - the list's month */
  month: { type: String, required: true },
  /** Earliest default-rate version month - earlier months are not auto-close targets. '' = do not judge */
  firstPlanMonth: { type: String, default: '' },
});

const emit = defineEmits(['loaded']);

const UNAVAILABLE_TEXT = 'call-api 에 자동 마감 현황 API 가 아직 없습니다(배포 전).';
const RUN_TOOLTIP =
  '재실행 도구입니다. 마감 실패한 병원을 다음 자동 실행 전에 다시 돌리거나, 자동 마감을 끈 환경에서 돌릴 때 씁니다.';
const ATTENTION_HELP =
  '마감 실패·마감 중단·검토 필요 병원입니다. 누르면 그 병원 월 정산으로 갑니다. 마감 중단은 마감하던 서버가 중간에 멈춘 병원으로, 다음 자동 실행이나 [지금 마감 실행]이 다시 마감합니다. 검토 필요는 경고가 사라져도 자동 확정하지 않아 사람이 확정합니다. 비차단 경고(웹 테스트만 사용 등)만 있는 병원은 자동 확정되어 여기 나오지 않습니다.';

const asArray = (v) => (Array.isArray(v) ? v : []);
const count = (v) => (isNumber(v) ? v : 0);

/** '2026-09' -> '9월' (the full label is the title) */
const shortMonth = computed(() => (isValidMonth(props.month) ? `${Number(props.month.slice(5, 7))}월` : ''));

// ---------------------------------------------------------------------------
// Loading - only the last request wins, so a slow earlier response never overwrites a newer one
// ---------------------------------------------------------------------------

/** Received close status (for loadedMonth) */
const closeRuns = ref(null);
const loadedMonth = ref('');
const loading = ref(false);
/** Error shown in place of the status when nothing was received */
const loadError = ref('');
/** call-api not deployed (route missing 404 / 501) */
const unavailable = ref(false);
let loadSeq = 0;

async function load() {
  const month = props.month;
  if (!isValidMonth(month)) return;
  const my = ++loadSeq;
  loading.value = true;
  try {
    const res = await props.api.getCloseRuns(month);
    if (my !== loadSeq) return;
    closeRuns.value = res && typeof res === 'object' ? res : null;
    loadedMonth.value = month;
    loadError.value = '';
    unavailable.value = false;
    syncRunNotice();
    emit('loaded', closeRuns.value);
  } catch (err) {
    if (my !== loadSeq) return;
    if (isCallApiUnimplemented(err)) {
      unavailable.value = true;
      closeRuns.value = null;
      return;
    }
    const text = parseCallApiError(err, '자동 마감 현황을 불러오지 못했습니다.');
    // A failed reload of the same month keeps what was shown and only notifies
    if (closeRuns.value && loadedMonth.value === month) {
      message.error(text);
    } else {
      closeRuns.value = null;
      loadError.value = text;
    }
  } finally {
    if (my === loadSeq) loading.value = false;
  }
}

/** Called by the parent (page refresh, mock controls) */
function reload() {
  return load();
}

defineExpose({ reload });

// A new month clears the previous month's values (skeleton) and loads again
watch(
  () => props.month,
  (month) => {
    if (loadedMonth.value !== month) {
      closeRuns.value = null;
      loadError.value = '';
    }
    void load();
  },
  { immediate: true }
);

// ---------------------------------------------------------------------------
// Status line
// ---------------------------------------------------------------------------

/**
 * Runs started from this card via [지금 마감 실행] - month -> run_id. last_run is the latest run, auto or manual
 * (contract 4-1), so this run is labelled manual even when the server sends no trigger. Kept after the notice closes.
 */
const manualRunIds = ref({});

/** Text and tone share one judgement (settlementRules) */
const summaryOpts = computed(() => ({
  firstPlanMonth: props.firstPlanMonth,
  manualRunId: manualRunIds.value[props.month] ?? null,
}));

const lastRun = computed(() => closeRuns.value?.auto_close?.last_run ?? null);
const autoCloseEnabled = computed(() => closeRuns.value?.auto_close?.enabled !== false);
const hasRun = computed(() => !!lastRun.value);

/** Sentence before any run: scheduled / waiting / not a target / disabled */
const summaryText = computed(() => autoCloseSummaryText(closeRuns.value, props.month, Date.now(), summaryOpts.value));

/** 'ok' | 'warn' (disabled, scheduled time passed, people needed) | 'info' (before close, not a target) */
const lineTone = computed(() => autoCloseSummaryTone(closeRuns.value, props.month, Date.now(), summaryOpts.value));

/** Status counts - from `counts`, or from last_run when counts are missing. Shown once a run exists or anything moved */
const countItems = computed(() => {
  const c = closeRuns.value?.counts;
  const lr = lastRun.value;
  let items;
  if (c) {
    items = [
      { key: 'confirmed', label: '확정', value: count(c.confirmed_system) + count(c.confirmed_human), tone: 'confirmed' },
      { key: 'review', label: '검토 필요', value: count(c.review), tone: 'review' },
      { key: 'failed', label: '실패', value: count(c.failed), tone: 'failed' },
      { key: 'closing', label: '마감 중', value: count(c.closing), tone: 'closing', optional: true },
      { key: 'estimating', label: '추정 중', value: count(c.estimating), tone: 'estimating', optional: true },
    ];
  } else if (lr) {
    items = [
      { key: 'confirmed', label: '확정', value: count(lr.confirmed), tone: 'confirmed' },
      { key: 'review', label: '검토 필요', value: count(lr.review), tone: 'review' },
      { key: 'failed', label: '실패', value: count(lr.failed), tone: 'failed' },
    ];
  } else {
    return [];
  }
  const moved = items.some((it) => it.key !== 'estimating' && it.value > 0);
  if (!hasRun.value && !moved) return [];
  return items.filter((it) => !it.optional || it.value > 0);
});

/** Companies before their settlement start date this month (counts.before_start; missing on an older call-api = 0) */
const beforeStartCount = computed(() => count(closeRuns.value?.counts?.before_start));
const BEFORE_START_TOOLTIP =
  '정산 시작일 전 달인 병원입니다. 자동 마감·청구를 하지 않아 위 상태별 수에 들지 않습니다.';

/** Small flags after the counts - what kind of run the counts come from */
const flags = computed(() => {
  const out = [];
  const lr = lastRun.value;
  if (lr) {
    const kind = lastRunKind(lr, { manualRunId: summaryOpts.value.manualRunId, enabled: autoCloseEnabled.value });
    if (kind.manual) out.push({ key: 'manual', label: kind.oneCompany ? '수동 실행 · 병원 1곳' : '수동 실행', tone: 'info' });
    if (kind.error) out.push({ key: 'error', label: '중단된 실행', tone: 'failed' });
    if (!autoCloseEnabled.value) out.push({ key: 'off', label: '자동 마감 꺼짐', tone: 'warn' });
  }
  return out;
});

/**
 * Tooltip - the month's counts with the auto / human split and the population, then the last run record.
 * Once a run exists the summary sentence ('자동 마감 <time> — 확정 n(자동 n · 사람 n) · …', manual / disabled label)
 * is not on screen any more, so it leads the tooltip.
 */
const detailLines = computed(() => {
  const c = closeRuns.value?.counts;
  const total = c
    ? ['estimating', 'closing', 'review', 'confirmed_system', 'confirmed_human', 'failed'].reduce(
        (sum, key) => sum + count(c[key]),
        0
      )
    : null;
  const population = total === null ? '' : `이 달 대상 ${formatNumber(total)}곳`;
  if (!hasRun.value) {
    return c ? [`${population} · ${closeCountsText(c)}`] : [];
  }
  return [
    [summaryText.value, population].filter(Boolean).join(' · '),
    ...lastRunTooltipLines(lastRun.value, {
      manualRunId: summaryOpts.value.manualRunId,
      enabled: autoCloseEnabled.value,
    }),
  ].filter(Boolean);
});

// ---------------------------------------------------------------------------
// Hospitals that need a person - failed and interrupted first, response order (company name) inside
// ---------------------------------------------------------------------------

function chipTooltip(item, chip) {
  // failed / interrupted: '마감 실패 · <error_message>' (prefixed '삭제된 회사 · ' for deleted companies)
  if (chip.status === 'failed' || chip.status === 'closing') return chip.tooltip;
  const deleted = chip.deleted ? `${DELETED_COMPANY_LABEL} · ` : '';
  if (!asArray(item?.blocking_codes).length) {
    return `${deleted}${chip.statusLabel} · 남은 차단 경고 없음 — 확정은 사람이 합니다`;
  }
  // '검토 필요 · 남은 차단 경고: 미종료 통화(NOT_FINAL_CALLS), …' - status first, before the deleted prefix
  return `${deleted}${chip.statusLabel} · ${chip.reason}`;
}

const attentionGroups = computed(() => {
  const groups = { failed: [], closing: [], review: [] };
  asArray(closeRuns.value?.attention).forEach((item, index) => {
    const chip = attentionChip(item);
    const status = chip.status === 'failed' || chip.status === 'closing' ? chip.status : 'review';
    groups[status].push({
      key: chip.companyId ?? `${chip.label}-${index}`,
      text: status === 'review' && item?.reopened ? `${chip.label}(재수정)` : chip.label,
      status,
      deleted: chip.deleted,
      to: chip.companyId
        ? settlementDetailLocation(chip.companyId, props.month, undefined, { companyName: item?.company_name })
        : null,
      tooltip: chipTooltip(item, chip),
    });
  });
  return [
    { key: 'failed', label: '마감 실패', items: groups.failed },
    { key: 'closing', label: CLOSE_INTERRUPTED_LABEL, items: groups.closing },
    { key: 'review', label: '검토 필요', items: groups.review },
  ].filter((g) => g.items.length);
});

// ---------------------------------------------------------------------------
// [지금 마감 실행] - ended months only (the server also refuses with 409 MONTH_NOT_ENDED)
// ---------------------------------------------------------------------------

const starting = ref(false);
/** { month, runId, state: 'running'|'done', result } - matched against last_run on the next load */
const runNotice = ref(null);

/** Why it is disabled for a month that has not ended (same rule and text as [지금 다시 실행] on the settle tab) */
const runDisabledReason = computed(() => closeRunDisabledReason(props.month));
const canRun = computed(() => !runDisabledReason.value && !unavailable.value);
const runTooltip = computed(() => {
  if (runDisabledReason.value) return runDisabledReason.value;
  if (unavailable.value) return UNAVAILABLE_TEXT;
  return RUN_TOOLTIP;
});

function confirmRun() {
  if (!canRun.value || starting.value) return;
  const month = props.month;
  Modal.confirm({
    title: '지금 마감 실행',
    content: closeRunConfirmText(month),
    okText: '마감 실행',
    cancelText: '취소',
    onOk: () => startRun(month),
  });
}

async function startRun(month) {
  starting.value = true;
  try {
    const res = await props.api.startCloseRun({ month });
    message.success('마감 실행을 시작했습니다. 끝나면 새로고침으로 결과를 확인하세요.');
    const runId = res?.run_id ?? null;
    runNotice.value = { month, runId, state: 'running', result: null };
    if (typeof runId === 'string' && runId) manualRunIds.value = { ...manualRunIds.value, [month]: runId };
    // Picked hospitals show up as "마감 중" right away - reload once (no polling)
    if (month === props.month) void load();
  } catch (err) {
    const code = getCallApiErrorCode(err);
    if (code === 'MONTH_NOT_ENDED') {
      message.warning('아직 끝나지 않은 달입니다. 끝난 달에서만 실행할 수 있습니다.');
    } else if (code === 'BILLING_INDEXES_NOT_READY') {
      message.warning(BILLING_INDEXES_NOT_READY_TEXT);
    } else {
      message.error(
        parseCallApiError(err, '마감 실행을 시작하지 못했습니다.', {
          unavailableMessage: 'call-api 에 마감 실행 API 가 아직 없습니다(배포 전).',
        })
      );
    }
  } finally {
    starting.value = false;
  }
}

/** Align the run notice with the new status - show the result once that run finished as last_run */
function syncRunNotice() {
  const notice = runNotice.value;
  if (!notice || notice.state !== 'running' || notice.month !== loadedMonth.value) return;
  const lr = closeRuns.value?.auto_close?.last_run ?? null;
  const finishedThisRun = notice.runId
    ? lr?.run_id === notice.runId && isNumber(lr?.finished_at)
    : count(closeRuns.value?.counts?.closing) === 0 && isNumber(lr?.finished_at);
  if (!finishedThisRun) return;
  runNotice.value = {
    ...notice,
    state: 'done',
    result: `확정 ${formatNumber(count(lr.confirmed))} · 검토 필요 ${formatNumber(count(lr.review))} · 실패 ${formatNumber(
      count(lr.failed)
    )} · 건너뜀 ${formatNumber(count(lr.skipped))}`,
  };
}

const runNoticeText = computed(() => {
  const notice = runNotice.value;
  if (!notice) return '';
  if (notice.state === 'done') {
    return `마감 실행이 끝났습니다 — ${notice.result}. 이미 확정된 병원과 사람이 손댄 검토 필요 병원은 건너뜁니다.`;
  }
  const id = notice.runId ? `(실행 ID ${notice.runId})` : '';
  return `마감 실행을 시작했습니다${id}. 끝나면 새로고침으로 결과를 확인하세요.`;
});
</script>

<style scoped lang="scss">
@use '../styles/settlement' as *;

// Interrupted close (lease expired) - distinct from failed (red) and review (orange)
$interrupted: #c41d7f;
// Status colours: review orange, confirmed green, closing blue, failed red, estimating gray
$tone-colors: (
  confirmed: $st-ok,
  review: $st-warning,
  failed: $st-danger,
  closing: $st-accent,
  estimating: $st-muted,
);

.close-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 18px;
  border: 1px solid $st-border;
  border-radius: 12px;
  background: #fff;
  color: $st-text;

  &__main {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: $st-text;
    white-space: nowrap;
  }

  &__status {
    display: flex;
    flex: 1 1 auto;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    min-width: 0;
    font-size: 13.5px;
    font-variant-numeric: tabular-nums;
  }

  &__run {
    margin-left: auto;
  }

  &__state {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 10px;
    font-size: 13px;
    color: $st-text-secondary;

    &.is-error {
      color: $st-danger;
    }
  }

  &__state-hint {
    font-size: 12px;
    color: $st-muted;
  }

  &__skeleton {
    width: 240px !important;
  }

  &__counts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__count {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: $st-text-secondary;

    strong {
      font-weight: 700;
      color: $st-text;
    }

    @each $tone, $color in $tone-colors {
      &.is-#{$tone} .close-card__dot {
        background: $color;
      }

      &.is-#{$tone}:not(.is-zero) strong {
        color: $color;
      }
    }

    &.is-zero {
      .close-card__dot {
        opacity: 0.35;
      }

      strong {
        font-weight: 500;
        color: $st-muted;
      }
    }
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }

  &__text {
    color: $st-text-secondary;

    &.is-warn {
      color: $st-warning;
      font-weight: 600;
    }
  }

  &__flag {
    @include st-pill;
    font-weight: 500;

    &.is-failed {
      @include st-pill-tone($st-danger, $st-danger-bg, $st-danger-border);
    }

    &.is-warn {
      @include st-pill-tone($st-warning, $st-warning-bg, $st-warning-border);
    }
  }

  &__help {
    font-size: 13px;
    color: $st-muted;
    cursor: help;

    &:focus-visible {
      outline: 2px solid $st-accent;
      outline-offset: 2px;
      border-radius: 50%;
    }
  }

  &__spin {
    line-height: 0;
  }

  &__attention {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px 18px;
    padding-top: 10px;
    border-top: 1px solid $st-border;
    font-size: 13px;
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12.5px;
    font-weight: 600;
    color: $st-text-secondary;
    white-space: nowrap;
  }

  &__group {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 8px;
  }

  &__group-label {
    font-size: 12.5px;
    font-weight: 600;

    &.is-failed {
      color: $st-danger;
    }

    &.is-closing {
      color: $interrupted;
    }

    &.is-review {
      color: $st-warning;
    }
  }

  &__links {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    margin: 0;
    padding: 0;
    list-style: none;

    > li + li::before {
      content: '·';
      margin-right: 10px;
      color: $st-border-strong;
    }
  }

  &__notice {
    font-size: 13px;
  }
}

// Hospital link - opens that hospital's month (settlement tab)
.attention-link {
  font-weight: 600;
  color: #2050b8;
  text-decoration: none;

  &:hover {
    color: #163c8c;
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid $st-accent;
    outline-offset: 2px;
    border-radius: 2px;
  }

  &.is-plain {
    color: $st-text;
    cursor: help;

    &:hover {
      text-decoration: none;
    }
  }

  // Deleted company - small gray suffix
  &__deleted {
    margin-left: 4px;
    font-size: 11.5px;
    color: $st-muted;
  }
}
</style>
